import './../../shop/modules/services.tracker';
import './../../shop/modules/multi_checkout/services.multi-checkout';
import './../../shop/modules/services.browser';
import './../../shop/modules/services.yup-helper';
import * as yup from 'yup';
import { CHECKOUT_EVENTS } from '../../shop/constants/angular-events';
import confirmModalTemplate from '../../../../../public/themes/shared/multi_checkout/templates.confirm-payment-type.modal.html';
import deliverySectionTemplate from '../../../../../public/themes/shared/multi_checkout/templates.delivery-section.html';
import {
  sortCartsByTags,
  checkSkipDestination,
  selectedDeliveriesFilter,
} from './utils/common';
import { deliveryOptionSchema } from './yupSchemas/common';
import { CART_SECTION, scrollToSection } from './utils/cart-scroll.helper';
import {
  SECTION_STATUS,
  COLLAPSE_STATUS,
} from '../../shop/constants/components/collapse-section-status';
import {
  BUTTON_STATUS,
  CART_STEPS,
  NEXT_STEP_EVENT_TYPE,
} from './constants/multi-checkout';
import { CART_TRACKER_TYPE_FOR_V1_AND_V2 } from '../../shop/constants/tracker';

const getDeliveriesYupSchema = (deliveries) => {
  const schemaObject = Object.values(deliveries).reduce((schema, delivery) => {
    return {
      ...schema,
      ...deliveryOptionSchema(delivery),
    };
  }, {});
  return yup.object(schemaObject);
};

const getInitState = () => ({
  // api data
  deliveryData: {},

  // ui status
  buttonStatus: BUTTON_STATUS.ENABLED,
  sectionStatus: SECTION_STATUS.ACTIVE,
  collapseStatus: COLLAPSE_STATUS.EXPANDED,
  isLoading: false,
  errors: {},
  sectionError: '',
  isNextButtonClicked: false,
});

app.directive('deliverySection', [
  '$sanitize',
  '$uibModal',
  '$filter',
  'yupHelper',
  'browserService',
  'multiCheckoutService',
  'trackerService',
  function (
    $sanitize,
    $uibModal,
    $filter,
    yupHelper,
    browserService,
    multiCheckoutService,
    trackerService,
  ) {
    return {
      restrict: 'E',
      templateUrl: deliverySectionTemplate,
      scope: {
        currentStep: '=',
        hasItemError: '&',
      },
      link: function (scope) {
        const hasAnyDeliveryChanged = (
          oldDeliveries,
          newDeliveries,
          cartTags = [],
        ) =>
          cartTags.some(
            (cartTag) =>
              oldDeliveries?.[cartTag.id]?.delivery_option_id !==
              newDeliveries?.[cartTag.id]?.delivery_option_id,
          );

        const getCartDeliveries = () => {
          scope.isLoading = true;
          scope.buttonStatus = BUTTON_STATUS.DISABLED;
          multiCheckoutService
            .getMultiCheckoutForm()
            .then((result) => {
              const newDeliveries = sortCartsByTags(
                result.cart_tags,
                result.deliveries,
              );
              if (
                hasAnyDeliveryChanged(
                  scope.deliveryData.deliveries,
                  newDeliveries,
                  result.cart_tags,
                )
              ) {
                scope.notifyFocusSection();
                scope.collapseStatus = COLLAPSE_STATUS.EXPANDED;
              }
              result.deliveries = newDeliveries;
              scope.deliveryData = result;
              scope.buttonStatus = BUTTON_STATUS.ENABLED;
              scope.sectionError = '';
            })
            .catch((error) => {
              scope.sectionError = error.message;
              scope.sectionStatus = SECTION_STATUS.ERROR;
              scope.buttonStatus = BUTTON_STATUS.DISABLED;
            })
            .finally(() => {
              scope.isLoading = false;
            });
        };

        // init state
        const init = () => {
          Object.assign(scope, getInitState());
          scope.paymentTypes = [
            {
              name: $filter('translate')(
                'multi_checkout.delivery_section.ncod',
              ),
              value: 'NCOD',
            },
            {
              name: $filter('translate')('multi_checkout.delivery_section.cod'),
              value: 'COD',
            },
          ];
          // fetch form
          getCartDeliveries();
          scope.multiCheckoutService = multiCheckoutService;
        };

        init();

        scope.nextButtonDisabled = function () {
          return scope.buttonStatus === 'disabled' || scope.hasItemError();
        };

        scope.validateDeliveries = () => {
          const schema = getDeliveriesYupSchema(scope.deliveryData.deliveries);

          const { isValid, errors } = yupHelper.yupValidation(
            schema,
            scope.deliveryData.deliveries,
          );

          scope.errors = errors;
          return isValid;
        };

        // event handlers
        scope.notifyFocusSection = () => {
          scope.sectionStatus = SECTION_STATUS.ACTIVE;
          scope.buttonStatus = BUTTON_STATUS.ENABLED;
          // notify other sections
          scope.$emit(CHECKOUT_EVENTS.CART.STEP.NEXT, {
            nextStep: CART_STEPS.DELIVERY,
            actionType: NEXT_STEP_EVENT_TYPE.FOCUS,
          });
        };

        scope.updateMultiCartForm = (payload) => {
          scope.$emit(CHECKOUT_EVENTS.CART.DELIVERY.CHANGING);
          scope.isLoading = true;
          scope.buttonStatus = BUTTON_STATUS.DISABLED;
          multiCheckoutService
            .updateMultiCheckoutForm(payload)
            .then((result) => {
              result.deliveries = sortCartsByTags(
                result.cart_tags,
                result.deliveries,
              );
              scope.deliveryData = result;
              scope.sectionError = '';
              scope.buttonStatus = BUTTON_STATUS.ENABLED;
              scope.sectionStatus = SECTION_STATUS.ACTIVE;
            })
            .catch((error) => {
              scope.sectionError = error.message;
              scope.buttonStatus = BUTTON_STATUS.DISABLED;
              scope.sectionStatus = SECTION_STATUS.ERROR;
            })
            .finally(() => {
              scope.isLoading = false;
              scope.errors = {};
              scope.$emit(CHECKOUT_EVENTS.CART.DELIVERY.CHANGED, {
                rerender: true,
              });
            });
        };

        scope.openPaymentTypeChangeConfirm = (newPaymentType) => {
          if (!scope.isNextButtonClicked) {
            scope.notifyFocusSection();
            scope.updateMultiCartForm({ payment_cod_type: newPaymentType });
            return;
          }
          const oldValue = scope.deliveryData.payment_cod_type;
          const modal = $uibModal.open({
            templateUrl: confirmModalTemplate,
            windowClass: 'confirm-payment-type-modal',
            controller: [
              '$scope',
              '$uibModalInstance',
              ($scope, $uibModalInstance) => {
                $scope.confirm = () => {
                  $uibModalInstance.close({ payment_cod_type: newPaymentType });
                };

                $scope.cancel = () => {
                  $uibModalInstance.dismiss();
                };
              },
            ],
          });

          modal.result
            .then((result) => {
              scope.notifyFocusSection();
              scope.updateMultiCartForm(result);
            })
            .catch(() => {
              // reset value when modal backdrop or cancel button is clicked
              scope.deliveryData.payment_cod_type = oldValue;
            });
        };

        scope.handleCountryChange = () => {
          scope.notifyFocusSection();
          const newCountry = scope.deliveryData.country;
          scope.updateMultiCartForm({ country: newCountry });
        };

        scope.handlePaymentTypeClick = (newPaymentType) => {
          if (scope.deliveryData.payment_cod_type === newPaymentType) {
            return;
          }
          scope.openPaymentTypeChangeConfirm(newPaymentType);
        };

        scope.handleDeliveryOptionsChange = () => {
          scope.notifyFocusSection();
          const newDeliveries = scope.deliveryData.deliveries;
          scope.updateMultiCartForm({ deliveries: newDeliveries });

          trackerService.track({
            type: CART_TRACKER_TYPE_FOR_V1_AND_V2.DELIVERY_OPTION_CLICK,
            data: {
              event_category: 'Cart',
            },
          });
        };

        scope.allowChoosePaymentTypes = () => {
          if (!multiCheckoutService.isToggleOn()) {
            return false;
          }
          return scope.deliveryData.allowed_payment_cod_types?.includes('COD');
        };

        scope.getDeliveryName = (delivery) => {
          const deliveryName = $filter('translateModel')(
            delivery.cart_name_translations,
          );
          const unnamed = $filter('translate')(
            'multi_checkout.cart_tag_unnamed',
          );
          return deliveryName || unnamed;
        };

        scope.getDeliveryDescription = (cart_tag_id, delivery) => {
          const deliveryOptions =
            scope.deliveryData.delivery_options[cart_tag_id] || [];
          const targetDelivery = deliveryOptions.find(
            (d) => d._id === delivery.delivery_option_id,
          );

          return {
            shouldShow: Boolean(targetDelivery?.show_description_on_checkout),
            descriptionTranslations: $sanitize(
              $filter('translateModel')(
                targetDelivery?.description_translations,
              ),
            ),
          };
        };

        function getCanSkipDestination() {
          const deliveryOptions = selectedDeliveriesFilter(
            scope.deliveryData.deliveries,
            scope.deliveryData.delivery_options,
          );
          return checkSkipDestination(deliveryOptions);
        }

        scope.onNextStepClick = () => {
          scope.isNextButtonClicked = true;
          const isValid = scope.validateDeliveries();

          if (!isValid) {
            scope.sectionStatus = SECTION_STATUS.ERROR;
            scope.buttonStatus = BUTTON_STATUS.DISABLED;
            return;
          }

          scope.collapseStatus = COLLAPSE_STATUS.COLLAPSED;
          scope.sectionStatus = SECTION_STATUS.DEFAULT;
          scope.buttonStatus = BUTTON_STATUS.DISABLED;

          const canSkipDestination = getCanSkipDestination();

          if (canSkipDestination) {
            scrollToSection(CART_SECTION.SUMMARY, {
              scrollDirectly: !browserService.isPad(),
            });
          }

          // notify other sections
          scope.$parent.$broadcast(CHECKOUT_EVENTS.CART.STEP.NEXT, {
            nextStep: canSkipDestination
              ? CART_STEPS.SUMMARY
              : CART_STEPS.DESTINATION,
            actionType: NEXT_STEP_EVENT_TYPE.NEXT,
          });
        };

        // event listeners
        scope.$parent.$on(CHECKOUT_EVENTS.CART.DELIVERY.RELOAD, () => {
          getCartDeliveries();
        });

        scope.$parent.$on(
          CHECKOUT_EVENTS.CART.STEP.NEXT,
          (_, { nextStep, actionType }) => {
            if (!nextStep) {
              return;
            }
            if (nextStep !== CART_STEPS.DELIVERY) {
              scope.sectionStatus = SECTION_STATUS.DEFAULT;

              if (actionType === NEXT_STEP_EVENT_TYPE.FOCUS) {
                scope.buttonStatus = BUTTON_STATUS.DISABLED;
                scope.collapseStatus = COLLAPSE_STATUS.COLLAPSED;
              }
            }
          },
        );
      },
    };
  },
]);
